import React from 'react';
import storeManager from '../../../store/StoreManager';
import uiConstants from '../../../constants/UIConstants';
import customFieldsService from '../../../services/CustomFieldsService';
import customFieldsConstants from '../../../constants/CustomFieldsConstants';
import utilService from '../../../services/UtilService';
import CompulsoryFrameLabel from '../../components/fjord/CompulsoryFrameLabel';
import sessionReader from '../../../store/readHelpers/SessionReader';
import assessmentReader from '../../../store/readHelpers/AssessmentReader';
import scorableItemNumberingService from '../../../services/questionView/ScorableItemNumberingService';
import fjordRubricsLoader from './fjordRubricsLoader';
import DefaultThemeService from '../default/DefaultThemeServices';
import uiService from '../../../services/UIService';

export default class FjordService extends DefaultThemeService {
    performPostComponentMountActions(selector) {
        this.scaleContent(selector);
        this.addAttributesToSVG(selector);
    }

    addAttributesToSVG(selector) {
        return setTimeout(() => {
            const SVGImage = document.querySelector(`${selector} .svg-interactions-container img`);
            if (SVGImage) {
                SVGImage.setAttribute('ondragstart', 'return false;');
                SVGImage.setAttribute('draggable', 'false');
            }
        }, 100);
    }

    getZoomFactor() {
        const { ui: { zoomFactor } } = storeManager.getState();
        switch (zoomFactor) {
        case uiConstants.ZOOM_FACTOR.MEDIUM:
            return 19 / 16; // zoom-medium font size / regular font size
        case uiConstants.ZOOM_FACTOR.LARGE:
            return 22 / 16; // zoom-large font size / regular font size
        default:
            return 1;
        }
    }

    scaleContent(selector) {
        const zoomFactor = this.getZoomFactor();
        if (zoomFactor === 1) {
            return false;
        }
        this.scaleOverlayContainers(selector, zoomFactor);
        return true;
    }

    scaleOverlayContainers(selector, zoomFactor) {
        return setTimeout(() => {
            const overlayContainers = document.querySelectorAll(`${selector} .overlayContainer`);
            Array.from(overlayContainers).forEach((container) => {
                const { top, left, width, height } = container.style;
                container.style.top = `${parseInt(top, 10) * zoomFactor}px`;
                container.style.left = `${parseInt(left, 10) * zoomFactor}px`;
                container.style.width = `${parseInt(width, 10) * zoomFactor}px`;
                container.style.height = `${parseInt(height, 10) * zoomFactor}px`;
            });
        }, 100);
    }

    scaleImage(imageId) {
        const zoomFactor = this.getZoomFactor();
        uiService.scaleImage(imageId, zoomFactor);
    }

    /**
     * In Fjord migration, parts can consist of multiple sections with the same section number. They should display as one part in the footer,
     * but function as two sections with each a stimulus and each a separate page for listing questions. The method sets mergedSections
     * in the assessment state, which is used for rendering the footer.
     * @param assessment
     */
    mergeSections(assessment) {
        const mergedSections = [];
        const parts = [];

        const sections = assessment.sections
            .filter((section) => {
                return section.questions.length > 0;
            });

        sections
            .forEach((section, index) => {
                const sectionClone = utilService.deepCloneObject(section);
                const { customFields = [] } = sectionClone;
                const partNumber = customFieldsService.getCustomFieldValue(customFields, customFieldsConstants.SECTION);

                const sectionNumber = partNumber || index + 1;

                // If the current part number has been handled, do nothing
                if (parts.includes(sectionNumber)) {
                    return;
                }
                parts.push(sectionNumber);

                // Create list of sections with the same part number
                const sectionsInPart = sections.filter((element) => {
                    const elementCustomFields = element.customFields || [];
                    const elementPartNumber = customFieldsService.getCustomFieldValue(elementCustomFields, customFieldsConstants.SECTION);
                    return elementPartNumber === sectionNumber;
                });

                if (sectionsInPart.length > 1) {
                    sectionsInPart.shift();
                } else {
                    // If the current section is the only with the part number, push the section as is
                    mergedSections.push(sectionClone);
                    return;
                }

                sectionsInPart.forEach((sectionToMerge) => {
                    const questionsToConcat = sectionToMerge.questions.map((question) => {
                        // Set mergedSectionId on the question for navigation from the footer
                        question.mergedSectionId = sectionToMerge.id;
                        return question;
                    });
                    sectionClone.questions = sectionClone.questions.concat(questionsToConcat);

                    // Set mergedSectionId on the section for showing merged sections as active in the footer
                    if (Array.isArray(sectionClone.mergedSectionIds)) {
                        sectionClone.mergedSectionIds.push(sectionToMerge.id);
                    } else {
                        sectionClone.mergedSectionIds = [sectionToMerge.id];
                    }
                });
                mergedSections.push(sectionClone);
            });

        assessment.mergedSections = mergedSections;
        return assessment;
    }

    getStimulusLayoutClassSelector(customFields) {
        const stimulusLayoutValue = customFieldsService.getCustomFieldValue(customFields, customFieldsConstants.STIMULUS_LAYOUT);
        let layoutClass;

        switch (stimulusLayoutValue) {
        case customFieldsConstants.stimulusLayoutValue.STANDARD:
            layoutClass = 'standard';
            break;
        case customFieldsConstants.stimulusLayoutValue.SINGLE_COLUMN_NOTICE:
            layoutClass = 'single-column s-c-notice';
            break;
        case customFieldsConstants.stimulusLayoutValue.SINGLE_COLUMN_TEXT:
            layoutClass = 'single-column s-c-text';
            break;
        case customFieldsConstants.stimulusLayoutValue.SINGLE_COLUMN_TEXT_WITH_LETTERS:
            layoutClass = 'single-column s-c-text-with-letters';
            break;
        case customFieldsConstants.stimulusLayoutValue.TWO_COLUMN_NOTICE:
            layoutClass = 'two-columns t-c-notice';
            break;
        case customFieldsConstants.stimulusLayoutValue.TWO_COLUMN_TEXT:
            layoutClass = 'two-columns t-c-text';
            break;
        case customFieldsConstants.stimulusLayoutValue.TWO_COLUMN_TEXT_WITH_LETTERS:
            layoutClass = 'two-columns t-c-text-with-letters';
            break;
        case customFieldsConstants.stimulusLayoutValue.FORM:
            layoutClass = 'form';
            break;
        default:
            layoutClass = '';
        }

        return layoutClass;
    }

    shouldHideScorableItemHeadline(question) {
        const { contentRevisionId } = question;
        return (
            customFieldsService.isSpeaking(question)
            || customFieldsService.isWriting(question)
            || !scorableItemNumberingService.getQuestionNumber(contentRevisionId)
        );
    }

    addQuestionWrapperClassNames(question, wrapperClasses) {
        return customFieldsService.isReading(question)
            ? wrapperClasses.push('reading-question')
            : false;
    }

    addQuestionClassNames(customFields, classNames) {
        const itemType = customFields && utilService.toCamelCaseRemoveNonAlphaNumeric(
            customFieldsService.getCustomFieldValue(customFields, customFieldsConstants.ITEM_TYPE)
        );
        const itemSubType = customFields && utilService.toCamelCaseRemoveNonAlphaNumeric(
            customFieldsService.getCustomFieldValue(customFields, customFieldsConstants.ITEM_SUB_TYPE)
        );
        if (itemType) {
            classNames.push(itemType);
        }
        if (itemSubType) {
            classNames.push(itemSubType);
        }
    }

    addGapChoiceClassNames(questionModel, styles, wrapperClassNames, classNames) {
        const isMatchingHeadings = customFieldsService.isMatchingHeadings(questionModel);
        const isFlowChartCompletionObjective = customFieldsService.isFlowChartCompletionObjective(questionModel);
        const isMatchingSentenceEnding = customFieldsService.isMatchingSentenceEnding(questionModel);
        const isMatching1 = customFieldsService.isMatching1(questionModel);

        if (!(isMatchingHeadings || isFlowChartCompletionObjective || isMatchingSentenceEnding || isMatching1)) {
            wrapperClassNames.push(styles.threeColumnGrid);
        }

        if (isMatchingHeadings) {
            classNames.push(styles.isMatchHeadings);
        }
        if (isFlowChartCompletionObjective) {
            classNames.push(styles.isFlowChartCompletionObjective);
        }
        if (isMatchingSentenceEnding) {
            classNames.push(styles.isMatchingSentenceEnding);
        }
        if (isMatching1) {
            classNames.push(styles.isMatching1);
        }
    }

    shouldShowSectionRubric(section, ui) {
        const focusLevel = customFieldsService.getCustomFieldValue(section.customFields, customFieldsConstants.TESTING_FOCUS_LEVEL_1_FJORD);
        const { TEST_TYPE_LISTENING, TEST_TYPE_READING, TEST_TYPE_WRITING } = customFieldsConstants;
        return (
            ui.variables.rubric__showSectionRubric
            && !!section.customFields
            && [TEST_TYPE_LISTENING, TEST_TYPE_READING, TEST_TYPE_WRITING].includes(focusLevel)
        );
    }

    shouldShowCompulsoryFrameLabel(section, question) {
        if (!customFieldsService.isSpeaking(question)
            || sessionReader.isContextExam()
            || sessionReader.isViewAsCandidate()) {
            return false;
        }

        return this.isNotFirstCompulsoryFrame(section, question);
    }

    isNotFirstCompulsoryFrame(section, question) {
        if (!this.isCompulsoryQuestion(question)) {
            return false;
        }

        const compulsoryQuestionIndex = section.questions
            .filter(this.isCompulsoryQuestion)
            .findIndex(questionItem => questionItem.contentItemId === question.contentItemId);

        return compulsoryQuestionIndex > 0;
    }

    getThemeQuestionLabel(section, question) {
        return this.shouldShowCompulsoryFrameLabel(section, question)
            ? <CompulsoryFrameLabel />
            : null;
    }

    getSystemPrintedVariable(identifier, qtiData) {
        if (identifier === 'section-number') {
            if (qtiData.nestedSectionId) {
                if (sessionReader.isContextPreview()) {
                    const nestedSection = assessmentReader.getNestedSectionById(qtiData.nestedSectionId);
                    if (nestedSection?.nonAdaptiveOrderNumberWithScorableQuestions > -1) {
                        return String(nestedSection.nonAdaptiveOrderNumberWithScorableQuestions);
                    }
                } else {
                    return qtiData.questionNumberingInSection;
                }
            }
            return '1';
        }
        return '[MISSING_SYSTEM_VARIABLE]';
    }

    isCompulsoryQuestion(question) {
        return customFieldsService.getCustomFieldValue(question.customFields,
            customFieldsConstants.SPEAKING_TYPE_IB) === customFieldsConstants.ITEM_COMPULSORY;
    }

    getMainQuestionWrapperLabel(label) {
        return label.scorableItemId;
    }

    getQuestionRubricsContent(question, choiceModel) {
        return fjordRubricsLoader.getRubricsContent(question, choiceModel);
    }
}
